import React, { memo, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle, useDerivedValue } from 'react-native-reanimated';

import { CircularPagerContext } from './CircularPager';
import { PageSlotContent } from './PageSlotContent';
import { ScrollableModalContextProvider } from './ScrollableModalContextProvider';
import { wrapToHalfLength } from './circular-helpers';

type Props = {
    offset: number;
};
/**
 * This component represents a page slot in the circular pager.
 * It uses the `CircularPagerContext` to get the necessary information for rendering the page slot.
 * The offset prop is used to determine the position of the page slot.
 *
 * The component is memoized to avoid unnecessary re-renders.
 *
 * It animates the position of the page slot based on the offset and the current page animation value.
 *
 * Each circular page slot animates itself. We don't actually need to animate the whole pager.
 */
export const CircularPageSlot = memo(
    function CircularPageSlot({ offset }: Props) {
        const pagerContext = useContext(CircularPagerContext);
        if (!pagerContext) {
            throw new Error('CircularPagerContext is not available');
        }

        const { pageAnim, pageWidth, pageGap } = pagerContext;

        const focus = useDerivedValue(() => {
            const rawDiff = offset - pageAnim.value;
            return wrapToHalfLength(rawDiff);
        });

        const slideAnimationStyle = useAnimatedStyle(() => {
            const clamped = Math.max(-1, Math.min(1, focus.value));
            const translateX = interpolate(
                clamped,
                [-1, 0, 1],
                [-(pageWidth.value + pageGap), 0, pageWidth.value + pageGap],
                Extrapolation.CLAMP
            );
            return {
                transform: [{ translateX }],
                // offset = 2 or -2 can overlap with position 1 or -1 when sliding through pages
                // set zIndex = -1 to push it behind and avoid visual overlap
                zIndex: focus.value === 2 || focus.value === -2 ? -1 : 0,
            };
        });

        const stableChildren = useMemo(
            () => (
                <ScrollableModalContextProvider>
                    <PageSlotContent offset={offset} />
                </ScrollableModalContextProvider>
            ),
            [offset]
        );

        return <Animated.View style={[styles.pageWrapper, slideAnimationStyle]}>{stableChildren}</Animated.View>;
    },
    // offset never changes after mount, so we skip re-renders
    () => true
);

export const SHEET_BORDER_RADIUS = 20;

const styles = StyleSheet.create({
    pageWrapper: {
        position: 'absolute',
        borderTopRightRadius: SHEET_BORDER_RADIUS,
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        overflow: 'hidden',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
});
